import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Card } from '@/components/ui/Card';
import { inputClass, labelClass, inlineErrorClass } from '@/components/ui/inputStyles';
import { MicroPromotionParamsPanel } from '@/components/MicroPromotionParamsPanel';
import { MicroPromotionEntriesPanel } from '@/components/MicroPromotionEntriesPanel';
import { MicroPromotionSensitiveWordsPanel } from '@/components/MicroPromotionSensitiveWordsPanel';
import { PromotionAreaScopeBar } from '@/components/PromotionAreaScopeBar';

type Area = {
  id: number;
  public_id: string;
  name: string;
  enabled: boolean;
};

export function PromotionAreaEditPage() {
  const { id: idParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = Number(idParam || 0);
  const [area, setArea] = useState<Area | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const viewRaw = searchParams.get('view');
  const pageView = viewRaw === 'entries' ? 'entries' : 'config';

  const switchView = useCallback(
    (next: 'config' | 'entries') => {
      const sp = new URLSearchParams(searchParams);
      sp.set('view', next);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    const r = await apiFetch<{ ok: boolean; area?: Area; err?: string }>('get_promotion_area', {
      method: 'GET',
      query: { id },
    });
    if (!r.ok || !r.area) {
      setErr('加载失败或无权访问');
      setArea(null);
    } else {
      setArea(r.area);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    if (id > 0) void load();
  }, [id, load]);

  async function saveName() {
    if (!area) return;
    setSaving(true);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_promotion_area', {
        method: 'POST',
        body: JSON.stringify({ id: area.id, name: area.name }),
      });
      if (!r.ok) setErr('保存失败');
    } finally {
      setSaving(false);
    }
  }

  if (id < 1) {
    return <p className={inlineErrorClass}>无效的区域 ID</p>;
  }

  if (loading) {
    return <Spinner label="加载推广区域" />;
  }

  if (!area) {
    return (
      <div className="space-y-3">
        <p className={inlineErrorClass}>{err || '未找到推广区域'}</p>
        <Link to="/promotion-areas" className="text-sm text-teal-700 underline">
          返回列表
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <nav className="text-sm text-slate-500">
        <Link to="/promotion-areas" className="text-teal-700 hover:underline">
          微推广管理
        </Link>
        <span className="mx-2">/</span>
        <span className="text-slate-800">{area.name}</span>
      </nav>

      <PromotionAreaScopeBar currentView={pageView} />

      <PageHeader
        title={area.name}
        description={
          <>
            区域 ID：<span className="font-mono text-slate-700">{area.public_id}</span>
            {!area.enabled ? (
              <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">已停用</span>
            ) : null}
          </>
        }
        actions={
          <Link to={`/promotion-areas?active_id=${id}&active_view=${pageView}`}>
            <Button type="button" variant="secondary">
              返回列表
            </Button>
          </Link>
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className={labelClass} htmlFor="area-name">
          区域名称
        </label>
        <div className="mt-1 flex flex-wrap gap-2">
          <input
            id="area-name"
            className={`${inputClass} max-w-md flex-1`}
            value={area.name}
            onChange={(e) => setArea({ ...area, name: e.target.value })}
          />
          <Button type="button" variant="primary" disabled={saving || !area.name.trim()} onClick={() => void saveName()}>
            {saving ? '保存中…' : '保存名称'}
          </Button>
        </div>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-600">将「参数配置」和「推广信息管理」分开展示，便于日常运营。</div>
          <div
            className="inline-flex rounded-lg border border-slate-200 bg-white p-1"
            role="tablist"
            aria-label="功能模块"
          >
            <button
              type="button"
              role="tab"
              aria-selected={pageView === 'config'}
              onClick={() => switchView('config')}
              className={`rounded-md px-4 py-2 text-sm font-medium ${
                pageView === 'config' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              参数配置
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={pageView === 'entries'}
              onClick={() => switchView('entries')}
              className={`rounded-md px-4 py-2 text-sm font-medium ${
                pageView === 'entries' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              推广信息管理
            </button>
          </div>
        </div>
      </Card>

      <MicroPromotionSensitiveWordsPanel areaId={area.id} />

      {pageView === 'config' ? (
        <MicroPromotionParamsPanel areaId={area.id} />
      ) : (
        <MicroPromotionEntriesPanel areaId={area.id} />
      )}
    </div>
  );
}
